import dayjs from "dayjs";
import secureLocalStorage from "react-secure-storage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";
import { faFileExcel, faMapLocation, faPrint, faUser } from "@fortawesome/free-solid-svg-icons";

//STYLES
import { Container } from "./styled";

//APP
import { useLogic } from "../../../../../../context/useLogic";
import { useAccess } from "../../../../../../context/useAccess";
import { obterLogo } from "../../../../precisao/mapaFertilidadeResumido/components/tabelaMapas/components/bookFertilidadeResumidoSeparado/components/capa/meta";
import { capitalizeWords } from "../../../../../../utils";
import { estruturaCompraInsumos } from "./meta";
import { gerarExcelCompraInsumos } from "./meta/gerarExcelCompraInsumos";
import { imprimirPdfCompraInsumos } from "./meta/gerarPDFCompraInsumos";
import { Item__Atividades, ItemPropriedades } from "../../../../../../services/atividades/GET";

//COMPONENTS
import Tabs from "../../../../../../Components/Tabs";
import Footer from "../../../../../../Components/Footer";
import ButtonCustom from "../../../../../../Components/ButtonCustom";
import { Warning } from "../../../../../../Components/CustomUi/warning";
import { PopSelect } from "../../../../../../Components/CustomUi/PopSelect";
import { Box } from "../../../../../../Components/CustomUi/box";
import { TitleH4 } from "../../../../../../Components/CustomUi/titleH4";
import { Switcher } from "../../../../../../Components/CustomUi/Switcher";

//PROPS
interface Props {
    data_propriedades: ItemPropriedades[];
    data_atividades: Item__Atividades[];
}

export const CompraInsumos: React.FC<Props> = ({ data_propriedades, data_atividades }) => {

    //CONST
    const { consultoria, nameCliente, nameSafra, codCultura } = useAccess();
    const { lisSafra, safraSelecionada } = useLogic();

    const nameCultura = useMemo(() => {
        if (!lisSafra || !safraSelecionada) return '';
        const safraAtual = lisSafra.find((safra) => safra.cod_cultura === codCultura);
        return safraAtual?.cultura?.[0]?.nome ?? '';
    }, [lisSafra, safraSelecionada, codCultura]);

    //STATES
    const [cod_propriedade_selecionada, setCod_propriedade_selecionada] = useState<number>(null);
    const [nome_propriedade_selecionada, setNome_propriedade_selecionada] = useState<string>('');
    const [gerar_por_propriedade, setGerar_por_propriedade] = useState<boolean>(false);
    const [tabSelecionado, setTabSelecionado] = useState<'Por cliente' | 'Por propriedade'>('Por cliente');
    const [exibir_tipo, setExibirTipo] = useState<boolean>(true);
    const [exibir_classe, setExibirClasse] = useState<boolean>(true);
    const [exibir_composicao, setExibirComposicao] = useState<boolean>(true);

    // PDF states
    const [gerandoPdf, setGerandoPdf] = useState(false);
    const [gerandoExcel, setGerandoExcel] = useState(false);

    useEffect(() => {
        if (!gerar_por_propriedade) {
            setCod_propriedade_selecionada(null);
            setNome_propriedade_selecionada('');
        }
    }, [gerar_por_propriedade])


    //#region GERAR PDF
    const gerarPDF = useCallback(async () => {
        setGerandoPdf(true);
        function nomeArquivo() {
            //@ts-ignore
            const data_atual = `__gerado_em_${dayjs().format('DD-MM-YYYY').replaceAll('-', '_')}`;
            //@ts-ignore
            const nome_safra = `__${capitalizeWords(nameSafra ?? '').replaceAll('-', '_').replaceAll(' ', '_')}`;
            //@ts-ignore
            const nome_cliente = capitalizeWords(nameCliente ?? '').replaceAll(' ', '_');
            //@ts-ignore
            const nome_propriedade = (nome_propriedade_selecionada !== '' && gerar_por_propriedade) ? `__Propriedade_${capitalizeWords(nome_propriedade_selecionada ?? '').replaceAll(' ', '_')}` : '';

            return `relatorio_insumos_totalizados_${nome_cliente}${nome_safra}${nome_propriedade}${data_atual}`;
        }
        try {
            await new Promise((resolve) => setTimeout(resolve, 0));
            const email_usuario_logado = secureLocalStorage.getItem('Itens')?.[0]?.email?.toLowerCase() || '';
            const estrutura_insumos = estruturaCompraInsumos(data_atividades, data_propriedades, cod_propriedade_selecionada, gerar_por_propriedade);
            const headerInfo = {
                logo: obterLogo() ?? consultoria?.logo_url,
                titulo: "Relatório de Insumos Totalizados",
                nome_arquivo: nomeArquivo(),
                razaoSocial: consultoria.linha1 ?? '',
                cliente: nameCliente || '',
                safra: nameSafra,
                cultura: nameCultura,
                autor: email_usuario_logado
            };

            await imprimirPdfCompraInsumos(estrutura_insumos, headerInfo, { exibir_tipo, exibir_classe, exibir_composicao });
        } catch (error) {
            console.error("Erro ao gerar PDF:", error);
        } finally {
            setGerandoPdf(false);
        }
    }, [data_atividades, data_propriedades, consultoria, nameCliente, nameSafra, nameCultura, nome_propriedade_selecionada, cod_propriedade_selecionada, gerar_por_propriedade, exibir_tipo, exibir_classe, exibir_composicao]);
    //#endregion

    //#region GERAR EXCEL
    const gerarExcel = useCallback(async () => {
        setGerandoExcel(true);
        try {
            const email_usuario_logado = secureLocalStorage.getItem('Itens')?.[0]?.email?.toLowerCase() || '';
            const estrutura_insumos = estruturaCompraInsumos(data_atividades, data_propriedades, cod_propriedade_selecionada, gerar_por_propriedade);

            const headerInfo = {
                logo: obterLogo() ?? consultoria?.logo_url,
                titulo: "Relatório de Compra de Insumos",
                razaoSocial: consultoria.linha1 ?? '',
                cliente: nameCliente || '',
                safra: nameSafra,
                cultura: nameCultura,
                autor: email_usuario_logado
            };

            await gerarExcelCompraInsumos(estrutura_insumos, headerInfo);
        } catch (error) {
            console.error("Erro ao gerar Excel:", error);
        } finally {
            setGerandoExcel(false);
        }
    }, [data_atividades, data_propriedades, consultoria, nameCliente, nameSafra, nameCultura, cod_propriedade_selecionada, gerar_por_propriedade]);


    useEffect(() => {
        setGerar_por_propriedade(tabSelecionado === 'Por propriedade');
    }, [tabSelecionado])


    return (
        <Container style={{ minHeight: 'calc(100vh - 130px)' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <Tabs
                    label="Forma de totalizar"
                    tabs={[{ descricao: 'Por cliente', icone: faUser }, { descricao: 'Por propriedade', icone: faMapLocation }]}
                    onChange={setTabSelecionado}
                    selecionado={tabSelecionado}
                    width={356}
                />
                {gerar_por_propriedade
                    ? <>
                        <PopSelect
                            label="Gerar de apenas uma propriedade"
                            empty_placeholder="Selecione a propriedade"
                            value={nome_propriedade_selecionada}
                            onChange={setNome_propriedade_selecionada}
                            onChangeId={setCod_propriedade_selecionada}
                            items={data_propriedades}
                            descricaoItem="nome"
                            idItem="cod"
                            icon={faMapLocation}
                        />

                        {nome_propriedade_selecionada
                            ? <Warning type="info">
                                Os insumos serão agrupados pela propriedade "{nome_propriedade_selecionada}".
                            </Warning>
                            : <Warning type="info">
                                Os insumos serão agrupados por propriedade.
                            </Warning>
                        }


                    </>
                    : <>
                        <Warning type="info">
                            Os insumos serão totalizados considerando todas as propriedades do cliente.
                        </Warning>
                    </>}


                <Box>
                    <TitleH4>Exibir colunas</TitleH4>

                    <Switcher
                        isChecked={true}
                        trigger={() => { }}
                        disabled
                        label="Nome do Insumo"
                    />
                    <Switcher
                        isChecked={exibir_tipo}
                        trigger={setExibirTipo}
                        label="Tipo de insumo"
                    />
                    <Switcher
                        isChecked={exibir_classe}
                        trigger={setExibirClasse}
                        label="Classe Agronômica"
                    />
                    <Switcher
                        isChecked={exibir_composicao}
                        trigger={setExibirComposicao}
                        label="Composição/Microrganismos"
                    />
                    <Switcher
                        isChecked={true}
                        trigger={() => { }}
                        disabled
                        label="Quantidade"
                    />
                </Box>

            </div>

            <Footer style={{ justifyContent: 'flex-start', gap: 10, width: '100%' }}>
                <ButtonCustom
                    icon={<FontAwesomeIcon icon={faFileExcel} />}
                    onClick={gerarExcel}
                    loading={gerandoExcel}
                >
                    Gerar Excel
                </ButtonCustom>
                <ButtonCustom
                    icon={<FontAwesomeIcon icon={faPrint} />}
                    onClick={gerarPDF}
                    type="secondary"
                    loading={gerandoPdf}
                >
                    Gerar PDF
                </ButtonCustom>
            </Footer>

            {/* O modal foi removido pois a impressão agora ocorre diretamente */}
        </Container>
    );
};